import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useShadowComparison } from "../../hooks/useShadowComparison";

const ALLOWED_EMAIL = "arsalanali@hlgsolutions.com";

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  borderRadius: "8px",
  padding: "20px",
  color: "var(--text-primary)",
};

function StatTile({ label, value, sub }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div style={cardStyle} className="h-100">
        <div className="text-secondary small mb-1">{label}</div>
        <div style={{ fontSize: "1.6rem", fontWeight: 600 }}>{value}</div>
        {sub && <div className="text-secondary small mt-1">{sub}</div>}
      </div>
    </div>
  );
}

const ShadowComparison = () => {
  const { user } = useAuth();
  const { isLoading, error, stats, items, refetch } = useShadowComparison();
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  // The real access control is server-side (routes/shadowComparisonRoutes.js
  // checks req.user.email from the verified JWT) - this is just so the page
  // doesn't render its contents for anyone else who navigates here directly.
  if (String(user?.email || "").toLowerCase().trim() !== ALLOWED_EMAIL) {
    return (
      <div className="p-4">
        <div className="alert alert-secondary">This page isn't available for your account.</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-white mb-1">Shadow Mode - Hybrid Deepgram + Qwen</h2>
      <p className="text-secondary mb-4">
        A sample of real production calls are silently run through the hybrid Qwen/Deepgram-fallback
        pipeline in the background. This never affects any call's actual disposition - it's purely
        observational data for deciding whether to switch the real pipeline over.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}

      {stats && (
        <div className="row g-3 mb-4">
          <StatTile label="Calls sampled" value={stats.total} sub={`${stats.errorCount} error(s)`} />
          <StatTile
            label="Flagged to Deepgram fallback"
            value={`${stats.flaggedPct}%`}
            sub={`${stats.flagged} of ${stats.total}`}
          />
          <StatTile label="Disposition match vs. production" value={`${stats.dispositionMatchPct}%`} />
          <StatTile label="Sub-disposition match vs. production" value={`${stats.subDispositionMatchPct}%`} />
        </div>
      )}

      {stats && (
        <div className="row g-3 mb-4">
          <StatTile label="All-Deepgram cost (sampled calls)" value={`$${stats.totalDgCost}`} />
          <StatTile label="All-Qwen cost (sampled calls)" value={`$${stats.totalQwenCost}`} />
          <StatTile label="Hybrid cost (sampled calls)" value={`$${stats.totalHybridCost}`} />
          <StatTile label="Hybrid savings vs. all-Deepgram" value={`${stats.savingsPct}%`} />
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-sm btn-outline-light" onClick={() => refetch(flaggedOnly)} disabled={isLoading}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
        <div className="form-check ms-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="flaggedOnly"
            checked={flaggedOnly}
            onChange={(e) => {
              setFlaggedOnly(e.target.checked);
              refetch(e.target.checked);
            }}
          />
          <label className="form-check-label text-secondary" htmlFor="flaggedOnly">
            Flagged calls only
          </label>
        </div>
      </div>

      <div style={cardStyle}>
        <div className="table-responsive">
          <table className="table table-sm table-borderless mb-0" style={{ fontSize: "0.85rem" }}>
            <thead>
              <tr className="text-secondary">
                <th>System Call ID</th>
                <th>Campaign</th>
                <th>Duration</th>
                <th>Production</th>
                <th>Hybrid</th>
                <th>Source</th>
                <th>Flagged</th>
                <th>Match</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it._id}>
                  <td>{it.systemCallId}</td>
                  <td>{it.campaignName}</td>
                  <td>{it.durationSec}s</td>
                  <td>
                    {it.prodDisposition}
                    {it.prodSubDisposition ? ` / ${it.prodSubDisposition}` : ""}
                  </td>
                  <td>
                    {it.hybridDisposition}
                    {it.hybridSubDisposition ? ` / ${it.hybridSubDisposition}` : ""}
                  </td>
                  <td>{it.hybridSource}</td>
                  <td>{it.flaggedForDeepgram ? "Yes" : "No"}</td>
                  <td>{it.status === "error" ? "error" : it.dispositionMatch ? "match" : "mismatch"}</td>
                  <td style={{ maxWidth: "260px" }}>{it.error || it.riskReasons || "-"}</td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={9} className="text-secondary text-center py-3">
                    {isLoading ? "Loading..." : "No shadow comparisons recorded yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShadowComparison;
